// ../../react-shim.js
import React from "react";

// src/use-counter.ts
import { useCallbackRef, useControllableProp } from "@nature-ui/hooks";

// ../utils/dist/index.esm.js
import React2 from "react";
import React22 from "react";
var isNotNumber = (value) => typeof value !== "number" || Number.isNaN(value) || !Number.isFinite(value);
var __DEV__ = process.env.NODE_ENV !== "production";
var _window;
try {
  _window = window;
} catch {
}
var getWindow = (node) => {
  var _a;
  return ((_a = node == null ? void 0 : node.ownerDocument) == null ? void 0 : _a.defaultView) ?? _window;
};
var checkIsBrowser = () => {
  const __window = getWindow();
  return Boolean(
    typeof __window !== "undefined" && __window.document && __window.document.createElement
  );
};
var isBrowser = checkIsBrowser();
var SELECTORS = [
  "input",
  "select",
  "textarea",
  "a[href]",
  "area[href]",
  "button",
  "[tabindex]",
  "audio[controls]",
  "video[controls]",
  "[contenteditable]:not([contenteditable=false])"
];
var selector = SELECTORS.join();
var warn = (options) => {
  if (options.condition && __DEV__) {
    console.warn(options.message);
  }
};
var minSafeInteger = Number.MIN_SAFE_INTEGER || -9007199254740991;
var maxSafeInteger = Number.MAX_SAFE_INTEGER || 9007199254740991;
var toNumber = (value) => {
  const num = parseFloat(value);
  return isNotNumber(num) ? 0 : num;
};
var toPrecision = (value, precision) => {
  let nextValue = toNumber(value);
  const _pres = precision ?? 10;
  const scaleFactor = 10 ** _pres;
  nextValue = Math.round(nextValue * scaleFactor) / scaleFactor;
  return precision ? nextValue.toFixed(precision) : nextValue.toString();
};
var countDecimalPlaces = (value) => {
  if (!Number.isFinite(value))
    return 0;
  let e = 1;
  let p = 0;
  while (Math.round(value * e) / e !== value) {
    e *= 10;
    p++;
  }
  return p;
};
var clampValue = (value, min, max) => {
  if (value === null)
    return value;
  warn({
    condition: max < min,
    message: "[Nature UI] clamp: max cannot be less than min"
  });
  return Math.min(Math.max(value, min), max);
};

// src/use-counter.ts
import { useCallback, useState } from "react";
var parse = (value) => parseFloat(value.toString().replace(/[^\w.-]+/g, ""));
var getDecimalPlaces = (value, step) => Math.max(countDecimalPlaces(step), countDecimalPlaces(value));
function cast(value, step, precision) {
  const parsedValue = parse(value);
  if (Number.isNaN(parsedValue))
    return void 0;
  const decimalPlaces = getDecimalPlaces(parsedValue, step);
  return toPrecision(parsedValue, precision ?? decimalPlaces);
}
var useCounter = (props = {}) => {
  const {
    onChange,
    precision: precisionProp,
    defaultValue,
    value: valueProp,
    step: stepProp = 1,
    min = minSafeInteger,
    max = maxSafeInteger,
    keepWithinRange = true
  } = props;
  const onChangeProp = useCallbackRef(onChange);
  const [valueState, setValue] = useState(() => {
    if (defaultValue == null)
      return "";
    return cast(defaultValue, stepProp, precisionProp) ?? "";
  });
  const [isControlled, value] = useControllableProp(valueProp, valueState);
  const decimalPlaces = getDecimalPlaces(parse(value), stepProp);
  const precision = precisionProp ?? decimalPlaces;
  const update = useCallback(
    (next) => {
      if (next === value)
        return;
      if (!isControlled) {
        setValue(next.toString());
      }
      onChangeProp == null ? void 0 : onChangeProp(next.toString(), parse(next));
    },
    [onChangeProp, isControlled, value]
  );
  const clamp = useCallback(
    (v) => {
      let nextValue = v;
      if (keepWithinRange) {
        nextValue = clampValue(nextValue, min, max);
      }
      return toPrecision(nextValue, precision);
    },
    [precision, keepWithinRange, max, min]
  );
  const increment = useCallback(
    (_step = stepProp) => {
      let next;
      if (value === "") {
        next = parse(_step);
      } else {
        next = parse(value) + _step;
      }
      next = clamp(next);
      update(next);
    },
    [clamp, stepProp, update, value]
  );
  const decrement = useCallback(
    (_step = stepProp) => {
      let next;
      if (value === "") {
        next = parse(-_step);
      } else {
        next = parse(value) - _step;
      }
      next = clamp(next);
      update(next);
    },
    [clamp, stepProp, update, value]
  );
  const reset = useCallback(() => {
    let next;
    if (defaultValue == null) {
      next = "";
    } else {
      next = cast(defaultValue, stepProp, precisionProp) ?? min;
    }
    update(next);
  }, [defaultValue, precisionProp, stepProp, update, min]);
  const castValue = useCallback(
    (v) => {
      const nextValue = cast(v, stepProp, precisionProp) ?? min;
      update(nextValue);
    },
    [precision, stepProp, update, min]
  );
  const valueAsNumber = parse(value);
  const isOutOfRange = valueAsNumber > max || valueAsNumber < min;
  const isAtMax = valueAsNumber === max;
  const isAtMin = valueAsNumber === min;
  return {
    isOutOfRange,
    isAtMax,
    isAtMin,
    precision,
    value,
    valueAsNumber,
    update,
    reset,
    increment,
    decrement,
    clamp,
    cast: castValue,
    setValue
  };
};
export {
  useCounter
};
